import { useEffect, useState, type FormEvent } from 'react';

import { IconEye } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { Modal } from '../../components/ui/Modal';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/datetime';
import { useAuditLogs } from './audit-logs.queries';
import type { AuditLogFilters, AuditLogRow, AuditLogType } from './types';

const initialFilters: AuditLogFilters = {
  page: 1,
  limit: 25,
  search: '',
  type: '',
  module: '',
  userId: '',
  fromDate: '',
  toDate: '',
};

const actionLabels: Record<AuditLogType, string> = {
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  login: 'Login',
  logout: 'Logout',
  other: 'Other',
};

const userLabel = (row: AuditLogRow) => {
  const user = row.userName || row.loginId || row.userId;
  const role = row.role || row.accountType;
  return role ? `${user} (${role})` : user;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const humanizeKey = (key: string) => key
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[-_.]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/^./, (letter) => letter.toUpperCase());

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString('en-IN');
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.length === 0 ? '-' : value.map(formatValue).join(', ');
  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '');
    return entries.length === 0 ? '-' : entries.map(([key, item]) => `${humanizeKey(key)}: ${formatValue(item)}`).join('; ');
  }
  return String(value);
};

const readableEntries = (value: unknown): Array<[string, unknown]> =>
  isPlainObject(value)
    ? Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '')
    : [];

const readAuditSection = (log: AuditLogRow | null, section: string) => {
  const payload = isPlainObject(log?.json) ? log.json : {};
  return payload[section];
};

const readAuditChanges = (log: AuditLogRow | null) => {
  const changes = readAuditSection(log, 'changes');
  return Array.isArray(changes)
    ? changes.filter((change): change is { field: string; from: unknown; to: unknown } =>
      isPlainObject(change) && typeof change.field === 'string' && 'from' in change && 'to' in change)
    : [];
};

const hasTrackedChanges = (log: AuditLogRow | null) => Array.isArray(readAuditSection(log, 'changes'));

const changeParts = (field: string) => {
  const [itemName, rawProperty] = field.split(' - ');
  if (!rawProperty) return { field, valueLabel: field };
  const valueLabel = rawProperty
    .replace(/^Unit quantity$/i, 'Unit')
    .replace(/^Sub unit quantity$/i, 'Sub Unit')
    .replace(/^item$/i, 'Item');
  return { field: itemName?.trim() || field, valueLabel };
};

export const AuditLogsPage = () => {
  const [draft, setDraft] = useState<AuditLogFilters>(initialFilters);
  const [filters, setFilters] = useState<AuditLogFilters>(initialFilters);
  const [selectedLog, setSelectedLog] = useState<AuditLogRow | null>(null);
  const query = useAuditLogs(filters);
  const busy = query.isFetching;
  const totalPages = Math.max(query.data?.pagination.totalPages ?? 1, 1);
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Audit logs could not be loaded.';

  useEffect(() => {
    if (filters.page > totalPages) {
      setFilters((current) => ({ ...current, page: totalPages }));
      setDraft((current) => ({ ...current, page: totalPages }));
    }
  }, [filters.page, totalPages]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setFilters({ ...draft, page: 1 });
  };

  const reset = () => {
    setDraft(initialFilters);
    setFilters(initialFilters);
  };

  const changePage = (page: number) => {
    setFilters((current) => ({ ...current, page }));
    setDraft((current) => ({ ...current, page }));
  };

  return (
    <div className="page-stack audit-log-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Administration</p>
          <h1>Audit Logs</h1>
          <p>Track user activity and record changes.</p>
        </div>
      </div>

      <section className="content-card audit-log-card">
        <form className="audit-log-filters" onSubmit={submit}>
          <label>
            Search User
            <input
              type="search"
              value={draft.search ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, search: event.target.value }))}
              placeholder="Name, login or user ID"
            />
          </label>
          <label>
            Type
            <select
              value={draft.type ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, type: event.target.value as '' | AuditLogType }))}
            >
              <option value="">All</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label>
            Module
            <input
              type="text"
              value={draft.module ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, module: event.target.value }))}
              placeholder="patients"
            />
          </label>
          <label>
            From
            <input
              type="date"
              value={draft.fromDate ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={draft.toDate ?? ''}
              onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))}
            />
          </label>
          <div className="audit-log-actions">
            <button className="button button-primary" type="submit" disabled={busy}>Search</button>
            <button className="button button-secondary" type="button" onClick={reset} disabled={busy}>Reset</button>
          </div>
        </form>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load audit logs" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.data && query.data.data.length === 0 ? <EmptyState title="No audit logs found" message="Updates made after this feature was added will appear here." /> : null}
        {query.data && query.data.data.length > 0 ? (
          <>
            <div className="audit-log-summary">
              <span>{query.data.pagination.total.toLocaleString('en-IN')} records</span>
              <label>
                Rows
                <select
                  value={filters.limit}
                  disabled={busy}
                  onChange={(event) => {
                    const limit = Number(event.target.value);
                    setDraft((current) => ({ ...current, limit, page: 1 }));
                    setFilters((current) => ({ ...current, limit, page: 1 }));
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
            <AuditLogTable rows={query.data.data} onViewJson={setSelectedLog} />
            <div className="pagination audit-log-pagination">
              <button className="button button-secondary" disabled={busy || filters.page <= 1} onClick={() => changePage(filters.page - 1)}>Previous</button>
              <span>Page {filters.page} of {totalPages}</span>
              <button className="button button-secondary" disabled={busy || filters.page >= totalPages} onClick={() => changePage(filters.page + 1)}>Next</button>
            </div>
          </>
        ) : null}
      </section>

      <Modal open={Boolean(selectedLog)} onClose={() => setSelectedLog(null)} labelledBy="audit-json-title" className="audit-json-modal">
        <header>
          <div>
            <p className="eyebrow">Audit details</p>
            <h2 id="audit-json-title">Updated Details</h2>
          </div>
          <button type="button" onClick={() => setSelectedLog(null)} aria-label="Close">X</button>
        </header>
        <AuditLogDetail log={selectedLog} />
      </Modal>
    </div>
  );
};

const AuditLogTable = ({ rows, onViewJson }: { rows: AuditLogRow[]; onViewJson: (row: AuditLogRow) => void }) => (
  <div className="audit-log-table-scroll">
    <table className="audit-log-table">
      <thead>
        <tr>
          <th>When</th>
          <th>Type</th>
          <th>User</th>
          <th>IP</th>
          <th>Module</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{formatDateTime(row.createdAt)}</td>
            <td><span className={`audit-type audit-type-${row.type}`}>{actionLabels[row.type]}</span></td>
            <td><strong>{userLabel(row)}</strong></td>
            <td>{row.ipAddress || '-'}</td>
            <td>{row.module}</td>
            <td>
              <button className="icon-button audit-json-button" type="button" onClick={() => onViewJson(row)} aria-label="View updated JSON" title="View updated JSON">
                <IconEye />
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AuditLogDetailSection = ({ title, entries, emptyMessage }: { title: string; entries: Array<[string, unknown]>; emptyMessage: string }) => (
  <section className="audit-detail-section">
    <h3>{title}</h3>
    {entries.length === 0 ? <p className="audit-detail-empty">{emptyMessage}</p> : (
      <dl className="audit-detail-grid">
        {entries.map(([key, value]) => (
          <div key={key}>
            <dt>{humanizeKey(key)}</dt>
            <dd>{formatValue(value)}</dd>
          </div>
        ))}
      </dl>
    )}
  </section>
);

const AuditLogChangesSection = ({ changes, tracked, recordNumber }: { changes: Array<{ field: string; from: unknown; to: unknown }>; tracked: boolean; recordNumber?: string }) => (
  <section className="audit-detail-section">
    <h3>Changed Fields</h3>
    {recordNumber ? <div className="audit-record-number"><span>Requisition number</span><strong>{recordNumber}</strong></div> : null}
    {changes.length === 0 ? (
      <p className="audit-detail-empty">
        {tracked ? 'No changes detected.' : 'Detailed before/after changes are available only for new requisition updates after this feature was enabled.'}
      </p>
    ) : (
      <div className="audit-change-table-wrap">
        <table className="audit-change-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>From</th>
              <th>To</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((change, index) => (
              <AuditChangeRow key={`${change.field}-${index}`} change={change} />
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

const AuditChangeRow = ({ change }: { change: { field: string; from: unknown; to: unknown } }) => {
  const parts = changeParts(change.field);
  return (
    <tr>
      <td>{parts.field}</td>
      <td>{parts.valueLabel} {formatValue(change.from)}</td>
      <td>{parts.valueLabel} {formatValue(change.to)}</td>
    </tr>
  );
};

const AuditLogDetail = ({ log }: { log: AuditLogRow | null }) => {
  if (!log) return null;
  const response = readAuditSection(log, 'response');
  const responseData = isPlainObject(response) && 'data' in response ? response.data : response;
  const responseEntries = readableEntries(responseData);
  const changes = readAuditChanges(log);
  const tracked = hasTrackedChanges(log);
  const isAuthEvent = log.type === 'login' || log.type === 'logout';
  const recordNumber = isPlainObject(responseData) ? formatValue(responseData.requisitionNumber) : undefined;

  return (
    <div className="audit-detail-body">
      <div className="audit-detail-summary">
        <div><span>Action</span><strong>{actionLabels[log.type]}</strong></div>
        <div><span>User</span><strong>{userLabel(log)}</strong></div>
        <div><span>Module</span><strong>{log.module}</strong></div>
        <div><span>IP</span><strong>{log.ipAddress || '-'}</strong></div>
      </div>

      {isAuthEvent ? (
        <AuditLogDetailSection title="Login Activity" entries={responseEntries} emptyMessage="No additional login details were recorded." />
      ) : (
        <>
          <AuditLogChangesSection changes={changes} tracked={tracked} {...(recordNumber ? { recordNumber } : {})} />
        </>
      )}
    </div>
  );
};
